import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/utils/supabase';
import type { Order } from '@/utils/supabase';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FaCircleArrowLeft } from "react-icons/fa6";
import { LuPackageCheck } from "react-icons/lu";
import { MdOutlineAutorenew } from "react-icons/md";
import { BsHourglassSplit } from "react-icons/bs";
import { TbTruckDelivery } from "react-icons/tb";
import {
  Check, AlertCircle, XCircle, MapPin, Calendar, ArrowRight, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useOrderStore } from '@/store/orderStore';

export function OrderTracking() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, currentLang } = useI18n();
  const [order, setOrder] = useState<Order | null>(null);
  const [trackingLogs, setTrackingLogs] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    // SWR Step 1: Instant cache hit from global orderStore
    const cachedOrder = useOrderStore.getState().orders.find(o => o.id === id);
    if (cachedOrder) {
      setOrder(cachedOrder);
      setIsLoading(false); // Render UI in 0ms — no spinner
    }

    // SWR Step 2: Silent background revalidation
    const fetchData = async () => {
      try {
        // Fetch fresh order data
        const { data: orderData, error: orderError } = await supabase
          .from('orders')
          .select('*')
          .eq('id', id)
          .single();

        if (orderError) throw orderError;
        setOrder(orderData); // Silently overwrite with latest network data

        // Fetch tracking logs (no global cache for these, always fresh)
        const { data: logsData, error: logsError } = await supabase
          .from('order_logs')
          .select('*')
          .eq('order_id', id)
          .order('created_at', { ascending: true });

        if (logsError) throw logsError;
        setTrackingLogs(logsData || []);
      } catch (err: unknown) {
        // Only show error if we have no cached fallback
        if (!cachedOrder) {
          console.error('Error fetching tracking data:', err);
          toast.error(t('error.fetch_tracking') || 'Could not fetch tracking details');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Subscribe to realtime order updates
    const channel = supabase
      .channel(`order-tracking-${id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'orders',
        filter: `id=eq.${id}`
      }, (payload) => {
        setOrder(payload.new as Order);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, t]);

  const steps = useMemo(() => [
    { id: 'pending', label: t('order.status.pending'), img: '/icons/order-placed.png', icon: BsHourglassSplit },
    { id: 'confirmed', label: t('order.status.confirmed'), img: '/icons/order-confirmed.png', icon: CheckCircle },
    { id: 'packaging', label: t('order.status.packaging'), img: '/icons/order-processing.png', icon: MdOutlineAutorenew },
    { id: 'shipped', label: t('order.status.shipped'), img: '/icons/order-shipped.png', icon: TbTruckDelivery },
    { id: 'delivered', label: t('order.status.delivered'), img: '/icons/order-delivered.png', icon: LuPackageCheck },
  ], [t]);

  const statusOrder = ['pending', 'confirmed', 'packaging', 'shipped', 'delivered'];
  const currentStatusIdx = statusOrder.indexOf(order?.status || 'pending');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          animate={{ scale: [1, 1.1, 1], rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="h-12 w-12 rounded-2xl border-4 border-primary border-t-transparent shadow-xl"
        />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mb-6 mx-auto">
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-black text-black mb-4">{t('error.order_not_found') || 'Order not found'}</h2>
          <Button onClick={() => navigate('/orders')} variant="default" className="rounded-full px-10 h-14 font-black shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90">
            {t('nav.orders')}
          </Button>
        </motion.div>
      </div>
    );
  }

  const currentStep = steps[currentStatusIdx] || steps[0];

  return (
    <div className="min-h-screen bg-[#FDFDFD] font-sans pb-32">
      {/* Premium Header */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
            onClick={() => navigate(`/orders`)}
          >
            <FaCircleArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-black tracking-tight text-center flex-1">
            {t('order.tracking.title')}
          </h1>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 pt-10 sm:pt-16 pb-20 overflow-visible">
        {/* Hero Section: Large Illustration */}
        <div className="flex flex-col items-center text-center mb-12 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={order.status}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="relative w-80 h-80 sm:w-80 sm:h-80 mb-8 overflow-hidden"
            >
              {/* Background Glow */}
              <div className="absolute inset-x-10 inset-y-10 bg-primary/20 blur-[80px] rounded-full animate-pulse" />

              <img
                src={currentStep.img}
                alt={currentStep.label}
                className="w-full h-full object-contain relative z-10 p-4 drop-shadow-[0_20px_40px_rgba(0,0,0,0.12)]"
                style={{ mixBlendMode: 'multiply' }}
              />
            </motion.div>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-2 relative z-10"
          >
            <h2 className="text-3xl font-black text-black tracking-tighter">
              {currentStep.label}
            </h2>
            <p className="text-gray-500 font-bold uppercase tracking-widest text-[11px] bg-gray-100/80 px-4 py-1.5 rounded-full inline-block">
              ORDER: #{order.id}
            </p>
          </motion.div>
        </div>

        {/* Dynamic Progress Bar Inset Card */}
        <Card className="border-none bg-white shadow-[0_24px_50px_rgba(0,0,0,0.06)] rounded-3xl sm:rounded-[32px] overflow-hidden mb-8 sm:mb-12 border border-gray-100/50 mx-[-8px] sm:mx-0">
          <CardContent className="p-4 sm:p-8 space-y-4 sm:space-y-6">
            <div className="flex items-center justify-between text-[10px] sm:text-xs font-black uppercase tracking-wider text-gray-700">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span>{new Date(order.created_at).toLocaleDateString(currentLang === 'bn' ? 'bn-BD' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <span>{order.delivery_address?.city || 'Dhaka'}</span>
              </div>
            </div>

            <div className="pt-2">
              {/* Premium Progress Bar */}
              <div className="relative h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStatusIdx / (statusOrder.length - 1)) * 100}%` }}
                  transition={{ duration: 1.5, ease: "circOut" }}
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary/80 to-primary rounded-full shadow-[0_0_15px_rgba(255,107,0,0.4)]"
                />
              </div>

              <div className="grid grid-cols-5 gap-1 text-[8px] sm:text-[9px] font-black uppercase tracking-tighter text-gray-400 mt-4">
                {steps.map((s, i) => (
                  <span key={s.id} className={`text-center transition-colors leading-tight ${i <= currentStatusIdx ? 'text-primary' : ''}`}>
                    {s.label}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interactive Timeline Vertically Centered */}
        <div className="space-y-0 relative border-l-2 border-gray-100/80 ml-8 pl-10 pt-4">
          {steps.map((step, idx) => {
            const stepIdx = statusOrder.indexOf(step.id);
            const isCompleted = stepIdx <= currentStatusIdx;
            const isCurrent = step.id === order.status;

            // Find timestamp
            let timestamp = null;
            if (step.id === 'pending') {
              timestamp = order.created_at;
            } else {
              const log = trackingLogs.find(l =>
                l.action === 'status_change' &&
                ((l.details as string)?.toLowerCase().includes(step.id) ||
                  (l.details as string)?.toLowerCase().includes(step.label.toLowerCase()))
              );
              if (log) timestamp = log.created_at as string;
            }

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * idx }}
                className={`relative pb-12 last:pb-0 group`}
              >
                {/* Checkpoint Dot */}
                <div className={`absolute left-[-50px] top-0 w-8 h-8 rounded-2xl flex items-center justify-center transition-all duration-500 shadow-sm border-2 ${isCompleted && !isCurrent ? 'bg-primary border-primary text-white' : 'bg-white border-gray-100 text-gray-400'
                  } ${isCurrent ? 'scale-125 ring-8 ring-primary/10 border-primary/20' : ''}`}
                >
                  {isCurrent ? (
                    <step.icon
                      className={`h-4 w-4 ${step.id === 'delivered' ? 'text-emerald-700 animate-pulse' :
                          step.id === 'shipped' ? 'text-blue-700 animate-truck-slide' :
                            step.id === 'pending' ? 'text-amber-700 animate-hourglass-flip' :
                              step.id === 'packaging' ? 'text-violet-700 animate-spin' :
                                step.id === 'confirmed' ? 'text-gray-700 animate-pulse' : ''
                        }`}
                      style={step.id === 'packaging' ? { animationDuration: '1.5s' } : {}}
                    />
                  ) : isCompleted ? (
                    <Check className="h-4 w-4" strokeWidth={4} />
                  ) : (
                    <step.icon className="h-3.5 w-3.5" />
                  )}
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <p className={`text-base font-black transition-colors ${isCompleted ? 'text-black' : 'text-gray-400'}`}>
                      {step.label}
                    </p>
                    <time className="text-[10px] font-bold text-gray-600 uppercase">
                      {timestamp ? new Date(timestamp).toLocaleTimeString(currentLang === 'bn' ? 'bn-BD' : 'en-US', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                    </time>
                  </div>

                  {isCurrent ? (
                    <div className="flex items-center gap-2">
                      <div className="flex space-x-1">
                        {[0, 1, 2].map(i => (
                          <motion.div
                            key={i}
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                            className="w-1 h-1 bg-primary rounded-full"
                          />
                        ))}
                      </div>
                      <p className="text-[10px] text-primary font-black uppercase tracking-widest leading-none">
                        {t('order.tracking.current')}
                      </p>
                    </div>
                  ) : isCompleted ? (
                    <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1">
                      <Check className="h-2.5 w-2.5" />
                      {t('order.tracking.completed')}
                    </p>
                  ) : null}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Cancelled State Handling with Premium Touch */}
        {order.status === 'cancelled' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-12 p-8 bg-red-50/50 backdrop-blur-sm border border-red-100 rounded-[32px] flex flex-col items-center text-center gap-4"
          >
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md shadow-red-500/10">
              <XCircle className="h-8 w-8 text-red-700 animate-pulse" />
            </div>
            <div className="space-y-1">
              <p className="text-xl font-black text-red-700">{t('order.status.cancelled')}</p>
              <p className="text-sm text-red-600/60 font-medium px-4">
                {currentLang === 'bn' ? 'অর্ডারটি বাতিল করার জন্য আমরা দুঃখিত। আপনার কোনো প্রশ্ন থাকলে আমাদের সাহায্য কেন্দ্রে যোগাযোগ করুন।' : 'We are sorry your order was cancelled. Please contact info center for details.'}
              </p>
            </div>
          </motion.div>
        )}

        {/* Footer Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-20 space-y-3"
        >
          <Button
            variant="default"
            className="w-full h-14 rounded-2xl font-black text-base shadow-xl shadow-primary/20 bg-primary hover:bg-primary/90 transition-all active:scale-[0.98] group"
            onClick={() => navigate(`/orders/${id}`)}
          >
            {t('btn.view_details')}
            <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-1" />
          </Button>
          <Button
            variant="ghost"
            className="w-full h-14 rounded-2xl font-black text-gray-500 hover:bg-gray-50 transition-all"
            onClick={() => navigate('/')}
          >
            {currentLang === 'bn' ? 'হোমে ফিরে যান' : 'Back to Home'}
          </Button>
        </motion.div>
      </main>
    </div>
  );
}
